import type { SdkUserAgentInfo } from '@bluecodecom/native';

/**
 * Per-app context an app hands to `trackingService.init` so the standard
 * super properties can be registered once, centrally, instead of every
 * wrapper re-implementing them (which is how 18 copies of `app_version` and a
 * stray `version` key came to exist).
 */
export type TrackingAppContext = {
  /**
   * Stable snake_case slug of the emitting app (`handyparken`, `vas_v2`,
   * `sdk_selector`, …). Lets analysts segment any event by app without parsing
   * the event-name prefix. Registry: `libs/tracking/catalog/oaf-event-catalog.yaml`.
   */
  app: string;
  /**
   * Human-readable mini name, for a mini-app only — omitted everywhere else.
   *
   * The catalog has carried `mini_app_name` as `proposed` since the framework
   * was written, noting it would be "a hardcoded constant in each mini's
   * tracking wrapper (the mini knows its own name); the SDK's banner/mini DTOs
   * carry no name field". FE-2671 is the first to send it, so it goes through
   * this builder rather than three `register` calls — a mini added later
   * inherits the property instead of having to remember it.
   *
   * Distinct from `app` on purpose even where the two strings match today:
   * `app` identifies the webview that emitted the event and exists on every
   * event in the estate, while `mini_app_name` marks the subset that happened
   * inside a mini and is what joins them to the SDK's own mini events.
   * Registry: `registries.mini_app_name`.
   */
  miniAppName?: string;
  /**
   * The canonical wallet host token — `BLUECODE`, `MONNI_APP`, `RAIFFEISEN` —
   * where the app has an authoritative source for it: a JWT `sdk_host` / `hst`
   * claim, or a launch parameter naming the host.
   *
   * Pass it and the builder registers `sdk_host` with
   * `sdk_host_source: 'app'`. Omit it and the builder falls back to the SDK
   * user agent's app-scheme segment, marked `sdk_host_source: 'user_agent'` —
   * see {@link buildStandardSuperProperties} for why that distinction is worth
   * a property of its own.
   *
   * Registered centrally rather than added per event, which is what six
   * wrappers used to do: `sdk_host` describes the session, not the event, and
   * a per-event copy is absent from every event an app forgets to route
   * through its wrapper (NAT-151).
   */
  sdkHost?: string;
};

/** Environment-derived inputs, injected so the builder stays pure and testable. */
export type StandardSuperPropertyInputs = {
  appVersion: string | undefined;
  deploymentTarget: string | undefined;
  platform: string;
  /** Raw `navigator.userAgent`, kept verbatim as `user_agent`. */
  userAgent: string | undefined;
  /** The same agent dissolved, or `null` outside an SDK webview. */
  userAgentInfo: SdkUserAgentInfo | null;
};

/**
 * Turn a user-agent app scheme into the shape the canonical `sdk_host` tokens
 * use — `bluecode` → `BLUECODE`, `raiffeisen-bluecode` → `RAIFFEISEN_BLUECODE`.
 *
 * A best-effort spelling fix, not a mapping: see the note on
 * `sdk_host_source` in {@link buildStandardSuperProperties}.
 */
const toSdkHostToken = (scheme: string) =>
  scheme
    .trim()
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

/**
 * Standard super properties every product-analytics event carries, per the
 * catalog's `global_properties`.
 *
 * Three groups:
 *
 * 1. **The app and the build** — `app`, `app_version`, `deployment_target`,
 *    `platform`, plus `mini_app_name` for a mini.
 * 2. **The dissolved user agent** — `host_app_name`, `host_app_version`,
 *    `host_app_build`, `sdk_version`, `sdk_build`, `os_version`, `device`,
 *    alongside the raw `user_agent` it was read from. Mini apps used to send
 *    the agent as that single opaque string, which Mixpanel's own UI cannot
 *    filter on (NAT-151). There is deliberately no `os` here: the OS *family*
 *    is `platform`, read out of the same segment, and a second copy of it
 *    would be one more property to keep in agreement.
 * 3. **The wallet host** — `sdk_host` and `sdk_host_source`.
 *
 * `sdk_host_source` exists because the two available sources are not equally
 * good, and the difference is invisible in the value. An app that passes
 * `sdkHost` supplies the canonical token the wallet and scheme-services use
 * (`BLUECODE`, `MONNI_APP`, `HUAWEI_WALLET`), so it is marked `app`. Where no
 * app-supplied value exists the token is derived from the user agent's
 * app-scheme segment, which agrees with the canonical token for Bluecode
 * itself and is *unverified for every other host* — `Raiffeisen Bluecode`
 * carries the token `RAIFFEISEN` while its scheme could well normalise to
 * something else. Those values are marked `user_agent` so one filter
 * separates the authoritative half of the series from the best-effort half,
 * instead of two spellings of one host quietly splitting a funnel.
 *
 * Undefined inputs are dropped rather than sent as `undefined` (Mixpanel would
 * store the string "undefined").
 */
export const buildStandardSuperProperties = (
  { app, miniAppName, sdkHost }: TrackingAppContext,
  {
    appVersion,
    deploymentTarget,
    platform,
    userAgent,
    userAgentInfo,
  }: StandardSuperPropertyInputs,
): Record<string, string> => {
  const props: Record<string, string> = { app, platform };

  if (miniAppName) {
    props['mini_app_name'] = miniAppName;
  }

  if (appVersion) {
    props['app_version'] = appVersion;
  }

  if (deploymentTarget) {
    props['deployment_target'] = deploymentTarget;
  }

  if (userAgent) {
    props['user_agent'] = userAgent;
  }

  if (userAgentInfo?.hostAppName) {
    props['host_app_name'] = userAgentInfo.hostAppName;
  }

  if (userAgentInfo?.hostAppVersion) {
    props['host_app_version'] = userAgentInfo.hostAppVersion;
  }

  if (userAgentInfo?.hostAppBuild) {
    props['host_app_build'] = userAgentInfo.hostAppBuild;
  }

  if (userAgentInfo?.sdkVersion) {
    props['sdk_version'] = userAgentInfo.sdkVersion;
  }

  if (userAgentInfo?.sdkBuild) {
    props['sdk_build'] = userAgentInfo.sdkBuild;
  }

  if (userAgentInfo?.osVersion) {
    props['os_version'] = userAgentInfo.osVersion;
  }

  if (userAgentInfo?.device) {
    props['device'] = userAgentInfo.device;
  }

  const derivedSdkHost = userAgentInfo?.hostAppScheme
    ? toSdkHostToken(userAgentInfo.hostAppScheme)
    : '';

  if (sdkHost) {
    props['sdk_host'] = sdkHost;
    props['sdk_host_source'] = 'app';
  } else if (derivedSdkHost) {
    props['sdk_host'] = derivedSdkHost;
    props['sdk_host_source'] = 'user_agent';
  }

  return props;
};

export default buildStandardSuperProperties;
